import { Link, makeStyles, mergeClasses } from '@fluentui/react-components';
import { PdfViewerComponent } from '@syncfusion/ej2-react-pdfviewer';
import React, { useEffect, useMemo, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { ISelectedEntity, resolveSelectedEntityForReport } from '../../../contexts/formViewContext';
import { useFormView } from '../../../contexts/useFormView';
import { findPrintReportModalNodeKeyForSourceItem, TaxReturnTOCItemWithRequiredNodeKey } from '../../../data-types/printReportModalTOCItem';
import { IWhitePaperColumn, ReviewFormDataType, TaxReturnTOCItem } from '../../../model';
import { findTocItemById, getTocItemFromKeyMap, isOverflowStatement } from '../../../utils/TaxReturnTOCUtils';
import LoadingSpinner from '../../common/LoadingSpinner';
import DefaultHtmlViewer from './DefaultHtmlViewer';
import DefaultPDFViewer from './DefaultPDFViewer';
import FormTOCSidebar, { CLOSED_TOC_SIDEBAR_WIDTH, TOC_SIDEBAR_WIDTH } from './FormTOCSidebar';
import FormViewerToolbar from './FormViewerToolbar';
import { HtmlFormInteractionHandler } from './HtmlFormInteractionHandler';
import { IHtmlViewerRef } from './HtmlMagnification';
import { PdfFormInteractionHandler } from './PdfFormInteractionHandler';
import { TableFormInteractionHandler } from './TableFormInteractionHandler';
import TableFormModal from './TableFormModal';

const useStyles = makeStyles({
    pdfContainer: {
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        flexDirection: 'column',
        border: '1px solid lightgray',
        borderRadius: '5px',
        height: '100%',
    },
    pdfContainerVisible: {
        display: 'flex',
    },
    pdfContainerHidden: {
        display: 'none',
    },
    errorMessage: {
        alignItems: 'center',
        fontWeight: 'bold',
    },
    downloadLink: {
        textDecoration: 'underline',
        cursor: 'pointer',
        overflow: 'hidden',
        marginLeft: '0.2rem',
        paddingTop: '1px',
    },
    pdfContent: {
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        flexDirection: 'column',
        height: '100%',
    },
    pdfContentVisible: {
        display: 'flex',
    },
    pdfContentHidden: {
        display: 'none',
    },
    htmlContent: {
        display: 'flex',
        justifyContent: 'center',
        overflow: 'auto',
        height: '100%',
        overflowY: 'scroll',
        overflowX: 'scroll',
    },
    htmlContentDrawerPinned: {
        width: `calc(100% - ${TOC_SIDEBAR_WIDTH}px)`,
    },
    htmlContentDrawerUnpinned: {
        width: `calc(100% - ${CLOSED_TOC_SIDEBAR_WIDTH}px)`,
    },
    loadingSpinnerContainer: {
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
});

export interface IFormViewerProps {
    returnKey: number | undefined;
    reportColumns: IWhitePaperColumn[];
    isTableDataReadyToDisplay: boolean;
    onPrintRequest?: (initialSelectedTocItems?: TaxReturnTOCItemWithRequiredNodeKey[]) => void;
}

const FormViewer: React.FC<IFormViewerProps> = ({ returnKey, reportColumns, isTableDataReadyToDisplay }) => {
    const styles = useStyles();
    const {
        loadTocData,
        isLoading,
        formData,
        tocData,
        showForm,
        hasError,
        hasAbortedOrTimedOutError,
        isTocItemPrinting,
        clearFormData,
        onPageChange,
        isDrawerPinned,
        setFormSetting,
        setFormZoomReady,
        formFields,
        hasTaxReturnTOCItems,
        formDataType,
        detailedWorkspace,
        showStatement,
        setShowStatement,
        currentTOCItemState,
        tocReferenceMap,
        setSelectedStatement,
        requestOverflowPrint,
        currentReport,
    } = useFormView();
    const pdfViewer = useRef<PdfViewerComponent>(null);
    const htmlViewer = useRef<IHtmlViewerRef>(null);
    const location = useLocation();
    const { t } = useTranslation();
    const searchParams: URLSearchParams = new URLSearchParams(location.search);
    const resolvedSelectedEntity: ISelectedEntity | undefined = resolveSelectedEntityForReport(searchParams, reportColumns);
    const resolvedSelectedEntityIndex: number = resolvedSelectedEntity?.index ?? 0;

    // Helper to check if current item is an overflow statement
    const isCurrentItemOverflowStatement = useMemo(() => {
        if (!currentTOCItemState?.id) return false;

        const currentItemFromKey: TaxReturnTOCItem | undefined = currentTOCItemState.tocKey
            ? getTocItemFromKeyMap(tocReferenceMap, currentTOCItemState.tocKey)
            : undefined;
        const currentItemFromId: TaxReturnTOCItem | undefined = currentItemFromKey
            ? undefined
            : findTocItemById(tocData?.items, currentTOCItemState.id);
        const currentItem: TaxReturnTOCItem | undefined = currentItemFromKey ?? currentItemFromId;
        if (!currentItem) return false;

        return isOverflowStatement([currentItem]);
    }, [currentTOCItemState?.id, currentTOCItemState?.tocKey, tocData?.items, tocReferenceMap]);

    // Compute whether the current TOC item is an overflow statement that's already being printed
    // Only show the message for overflow statements (large HTML statements that need downloading)
    const isTocItemAlreadyPrinting = useMemo(() => {
        if (!currentTOCItemState?.id || !returnKey) return false;
        return isTocItemPrinting(returnKey, currentTOCItemState?.tocKey) && isCurrentItemOverflowStatement;
    }, [currentTOCItemState?.tocKey, returnKey, isTocItemPrinting, isCurrentItemOverflowStatement]);

    const currentPrintSelectionOverride = useMemo((): TaxReturnTOCItemWithRequiredNodeKey[] | undefined => {
        const currentItemFromKey: TaxReturnTOCItem | undefined = currentTOCItemState?.tocKey
            ? getTocItemFromKeyMap(tocReferenceMap, currentTOCItemState.tocKey)
            : undefined;
        const currentItemFromId: TaxReturnTOCItem | undefined = currentItemFromKey
            ? undefined
            : findTocItemById(tocData?.items, currentTOCItemState?.id);
        const currentItem: TaxReturnTOCItem | undefined = currentItemFromKey ?? currentItemFromId;

        if (!currentItem || !tocData?.items) {
            return undefined;
        }

        const nodeKey: string | undefined = findPrintReportModalNodeKeyForSourceItem(tocData.items, currentItem);

        if (!nodeKey) {
            return undefined;
        }

        return [
            {
                ...currentItem,
                nodeKey,
            },
        ];
    }, [currentTOCItemState?.id, currentTOCItemState?.tocKey, tocData?.items, tocReferenceMap]);

    const handleOverflowDownloadRequest = (): void => {
        requestOverflowPrint(currentPrintSelectionOverride);
    };

    const formInteractionHandler = useMemo(() => {
        switch (formDataType) {
            case ReviewFormDataType.Html:
                return new HtmlFormInteractionHandler(htmlViewer);
            case ReviewFormDataType.Table:
                return new TableFormInteractionHandler();
            case ReviewFormDataType.Pdf:
            default:
                return new PdfFormInteractionHandler(pdfViewer);
        }
    }, [formDataType]);

    const onZoomChange = (zoom: { zoomValue: number }) => {
        if (typeof zoom?.zoomValue !== 'number' || !Number.isFinite(zoom.zoomValue)) {
            return;
        }

        setFormSetting('currentZoom', zoom.zoomValue);
        setFormZoomReady(true);
    };

    const loadedTocForReportIdRef = useRef<number | undefined>(undefined);

    useEffect(() => {
        return () => clearFormData();
    }, []);

    useEffect(() => {
        if (!currentReport || !isTableDataReadyToDisplay) {
            loadedTocForReportIdRef.current = undefined;
            return;
        }

        if (loadedTocForReportIdRef.current === currentReport.id) {
            return;
        }

        loadedTocForReportIdRef.current = currentReport.id;
        loadTocData(resolvedSelectedEntityIndex, {
            entity: resolvedSelectedEntity,
        });
    }, [currentReport, isTableDataReadyToDisplay, loadTocData, resolvedSelectedEntity, resolvedSelectedEntityIndex]);

    const shouldShowSidebar = (): boolean => {
        return showForm && Boolean(tocData?.items?.length);
    };

    const renderErrorMessage = () => {
        if (isCurrentItemOverflowStatement) {
            if (isTocItemAlreadyPrinting) {
                return t('statementAlreadyDownloading');
            }
            if (hasAbortedOrTimedOutError) {
                return (
                    <Trans
                        i18nKey='requestCancelledTooLargeWithLink'
                        components={{
                            l: <Link as='button' onClick={handleOverflowDownloadRequest} className={styles.downloadLink} />,
                        }}
                    />
                );
            }
        }
        return t('pdfDisplayError');
    };

    const renderContent = () => {
        return (
            <>
                {/* Always render PDF viewer in background when we have PDF data, even if modal is open */}
                {shouldShowPdfInBackground && (
                    <DefaultPDFViewer
                        ref={pdfViewer}
                        pdfData={formData}
                        isLoading={!isTableDataReadyToDisplay && isLoading}
                        showPdf={showForm}
                        onPageChange={onPageChange}
                        onZoomChange={onZoomChange}
                        width={isDrawerPinned ? `calc(100% - ${TOC_SIDEBAR_WIDTH}px)` : '100%'}
                        formFields={formFields}
                    />
                )}
                {/* Render other form types (HTML) */}
                {formDataType === ReviewFormDataType.Html && renderForm()}
            </>
        );
    };

    const closeStatementModal = () => {
        setShowStatement({ visible: false, isAdd: false });

        setSelectedStatement(undefined);
    };

    const renderForm = () => {
        switch (formDataType) {
            case ReviewFormDataType.Html:
                return (
                    <DefaultHtmlViewer
                        ref={htmlViewer}
                        htmlData={formData}
                        showHtml={showForm}
                        onZoomChange={onZoomChange}
                        width={isDrawerPinned ? `calc(100% - ${TOC_SIDEBAR_WIDTH}px)` : `calc(100% - ${CLOSED_TOC_SIDEBAR_WIDTH}px)`}
                    />
                );
            case ReviewFormDataType.Table:
                return (
                    <TableFormModal
                        isOpen={showStatement.visible}
                        onClose={closeStatementModal}
                        detailedWorkspace={detailedWorkspace}
                        isAdd={showStatement.isAdd}
                    />
                );
            default:
                return (
                    <DefaultPDFViewer
                        ref={pdfViewer}
                        pdfData={formData}
                        isLoading={!isTableDataReadyToDisplay && isLoading}
                        showPdf={showForm}
                        onPageChange={onPageChange}
                        onZoomChange={onZoomChange}
                        width={isDrawerPinned ? `calc(100% - ${TOC_SIDEBAR_WIDTH}px)` : '100%'}
                        formFields={formFields}
                    />
                );
        }
    };

    // Determine if we should show PDF in background (when we have PDF data and it's not HTML type)
    const shouldShowPdfInBackground = formData && formDataType !== ReviewFormDataType.Html;
    const hasAnyError = hasError || hasAbortedOrTimedOutError || isTocItemAlreadyPrinting;
    const isWaitingForFormContent: boolean = showForm && hasTaxReturnTOCItems && formData == null && formDataType == null && !hasAnyError;

    const pdfContainerClassName = mergeClasses(styles.pdfContainer, showForm ? styles.pdfContainerVisible : styles.pdfContainerHidden);
    const pdfContentClassName = mergeClasses(styles.pdfContent, showForm ? styles.pdfContentVisible : styles.pdfContentHidden);
    const htmlContentClassName = mergeClasses(
        styles.htmlContent,
        isDrawerPinned ? styles.htmlContentDrawerPinned : styles.htmlContentDrawerUnpinned
    );

    return (
        <>
            {hasTaxReturnTOCItems || isLoading || !isTableDataReadyToDisplay ? (
                <div className={pdfContainerClassName}>
                    <FormViewerToolbar formInteractionHandler={formInteractionHandler} reportColumns={reportColumns}></FormViewerToolbar>
                    <div id='form-viewer-container' className={pdfContentClassName}>
                        {shouldShowSidebar() ? <FormTOCSidebar /> : null}
                        {hasAnyError ? (
                            <div className={mergeClasses(htmlContentClassName, styles.errorMessage)}>{renderErrorMessage()}</div>
                        ) : isLoading || isWaitingForFormContent ? (
                            <div className={styles.loadingSpinnerContainer}>
                                <LoadingSpinner id='loadingViewFormSpinner' label={t('loadingPleaseWait').toString()} />
                            </div>
                        ) : (
                            renderContent()
                        )}
                    </div>
                    {/* Render modal as overlay, separate from main content */}
                    <TableFormModal
                        isOpen={showStatement.visible}
                        onClose={closeStatementModal}
                        detailedWorkspace={detailedWorkspace}
                        isAdd={showStatement.isAdd}
                    />
                </div>
            ) : (
                showForm && <div>{t('pdfNotAvailable')}</div>
            )}
        </>
    );
};

export default FormViewer;
